import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowsClockwise,
  Check,
  ClipboardText,
  Plus,
  X
} from '@phosphor-icons/react';
import { useTaskTypes } from '../../../hooks/useTaskTypes.js';
import { teamService } from '../../../services/teams/teamService.js';
import { taskService } from '../../../services/tasks/taskService.js';
import { teamDisableService } from '../../teams/services/teamDisableService.js';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog.jsx';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';
import { buildRouteColorMap, colorDistributionRoutes } from '../../../styles/routeColors.js';
import {
  TaskDistributionAutomateModal,
  TaskDistributionMapSection,
  TaskDistributionResultsSection,
  TaskDistributionStepper
} from '../components/index.js';
import {
  FIXED_ROUTING_PRIORITY_PRESET,
  buildMapArtifacts,
  countTaskStops,
  formatDateInput,
  getOptimizationCriterion,
  hasAssignedTeam,
  normalizeTaskTeamId
} from '../utils/taskDistributionUtils.jsx';

const FIXED_CONFIG_FIELDS = {
  algorithm: 'algorithm2',
  priorityMode: 'punctuality'
};

const DEFAULT_CONFIG = {
  ...FIXED_CONFIG_FIELDS,
  maxTasksPerTeam: 21,
  solverTimeoutSeconds: 15,
  shiftStartTime: '',
  optimizationCriterion: 'balanced'
};

const buildFixedConfig = (config = {}) => {
  const nextConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    ...FIXED_CONFIG_FIELDS
  };

  return {
    ...nextConfig,
    optimizationCriterion: getOptimizationCriterion(nextConfig.optimizationCriterion).value
  };
};

const TASKS_QUERY = {
  status: 'pendiente',
  pageCurrent: 0,
  pageSize: 5000
};

const NOTIFICATION_DURATION_MS = 5000;
const CONFIRMED_ROUTES_REFRESH_MS = 10000;
const MAX_DISTRIBUTION_LOOKAHEAD_DAYS = 4;

const getTaskTypeIdentifier = (task = {}) => (
  task.taskTypeId
  ?? task.TaskType?.name
  ?? task.taskType?.name
  ?? task.taskTypeName
);

const taskMatchesSelectedTypes = (task, taskTypeIds = []) => (
  !taskTypeIds.length
  || taskTypeIds.some((taskTypeId) => (
    String(taskTypeId || '') === String(getTaskTypeIdentifier(task) || '')
  ))
);

const getFilteredTaskIds = (tasks = [], taskTypeIds = []) => tasks
  .filter((task) => taskMatchesSelectedTypes(task, taskTypeIds))
  .map((task) => task.id);

const getPreviewTaskIds = (preview) => [...new Set([
  ...(preview?.assignments || []).flatMap((assignment) => (
    (assignment?.stops || [])
      .filter((stop) => stop?.type === 'TASK' && stop.taskId)
      .map((stop) => stop.taskId)
  )),
  ...(preview?.unassignedTasks || []).map((task) => task?.taskId).filter(Boolean)
])];

const canRestoreCachedPreview = (preview, tasks = []) => {
  const requestedTaskIds = getPreviewTaskIds(preview);

  if (!requestedTaskIds.length) {
    return false;
  }

  const availableTaskIds = new Set(tasks.map((task) => task.id));
  return requestedTaskIds.some((taskId) => availableTaskIds.has(taskId));
};

const parseTimestamp = (value) => {
  const parsedValue = new Date(value || '').getTime();
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getLatestConfirmedUpdateTimestamp = (distributionResult) => {
  const timestamps = (distributionResult?.assignments || [])
    .map((assignment) => parseTimestamp(assignment?.updatedAt))
    .filter((value) => value !== null);

  if (!timestamps.length) {
    return null;
  }

  return Math.max(...timestamps);
};

const shouldRestoreCachedPreview = (preview, confirmedDistributionResult) => {
  if (!preview) {
    return false;
  }

  const cachedAtTimestamp = parseTimestamp(preview.cachedAt);
  const latestConfirmedUpdateTimestamp = getLatestConfirmedUpdateTimestamp(confirmedDistributionResult);

  if (cachedAtTimestamp === null || latestConfirmedUpdateTimestamp === null) {
    return true;
  }

  return cachedAtTimestamp > latestConfirmedUpdateTimestamp;
};

const getRedisPersistenceWarningMessage = (warnings = []) => {
  const warningMessage = (warnings || []).find((warning) => {
    const normalizedWarning = String(warning || '').toLowerCase();
    return normalizedWarning.includes('redis no esta disponible')
      || normalizedWarning.includes('no se guardaran en cache');
  });

  if (!warningMessage) {
    return '';
  }

  return String(warningMessage)
    .replace(/^Redis no esta disponible\.\s*/i, '')
    .trim();
};

const getAppliedOptimizationCriterion = (distributionResult) => (
  distributionResult?.metadata?.appliedConfiguration?.optimizationCriterion
);

const buildConfirmationPayload = ({
  date,
  distributionResult,
  configuration,
  selectedTeamIds,
  selectedTaskIds,
  fixedTaskIds,
  selectedTaskTypeIds
}) => {
  const appliedConfiguration = distributionResult?.metadata?.appliedConfiguration || configuration;
  const payload = {
    date,
    previewRequest: {
      date,
      teamIds: selectedTeamIds,
      taskIds: selectedTaskIds,
      fixedTaskIds,
      taskTypeIds: selectedTaskTypeIds,
      redistributionTeamIds: selectedTeamIds,
      configuration: appliedConfiguration
    }
  };

  if (distributionResult?.cacheToken) {
    payload.previewCacheToken = distributionResult.cacheToken;
  }

  return payload;
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + Number(days || 0));
  return nextDate;
};

const normalizeId = (value) => String(value || '');

const buildDateOptions = (baseDate = new Date(), count = 5) => {
  const weekdayFormatter = new Intl.DateTimeFormat('es-AR', { weekday: 'long' });
  const dayFormatter = new Intl.DateTimeFormat('es-AR', { day: 'numeric' });

  return Array.from({ length: count }, (_, index) => {
    const optionDate = addDays(baseDate, index);
    const weekdayLabel = weekdayFormatter.format(optionDate);
    return {
      value: formatDateInput(optionDate),
      weekdayLabel: weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1),
      dayLabel: dayFormatter.format(optionDate)
    };
  });
};

const normalizeFixedTaskIds = (currentFixedTaskIds, tasks, selectedTaskIds) => {
  const selectablePinnedTaskIdSet = new Set(
    tasks
      .filter((task) => hasAssignedTeam(task) && selectedTaskIds.includes(task.id))
      .map((task) => task.id)
  );
  const persistedFixedTaskIds = tasks
    .filter((task) => (
      task.fixedTeam === true
      && hasAssignedTeam(task)
      && selectedTaskIds.includes(task.id)
    ))
    .map((task) => task.id);

  return Array.from(new Set([
    ...currentFixedTaskIds.filter((taskId) => selectablePinnedTaskIdSet.has(taskId)),
    ...persistedFixedTaskIds
  ]));
};

const areIdArraysEqual = (left = [], right = []) => (
  left.length === right.length
  && left.every((value, index) => String(value) === String(right[index]))
);

const buildRouteAssignmentVisibilityKey = (assignments = []) => assignments
  .map((assignment) => `${normalizeId(assignment.teamId)}:${assignment.route?.geometry ? '1' : '0'}`)
  .sort()
  .join('|');

const buildMapDisplayDistributionResult = (confirmedDistributionResult, previewDistributionResult) => {
  if (!confirmedDistributionResult) {
    return previewDistributionResult;
  }

  if (!previewDistributionResult) {
    return confirmedDistributionResult;
  }

  const previewTeamIdSet = new Set(
    (previewDistributionResult.assignments || []).map((assignment) => normalizeId(assignment.teamId))
  );
  const mergedAssignments = [
    ...(confirmedDistributionResult.assignments || []).filter(
      (assignment) => !previewTeamIdSet.has(normalizeId(assignment.teamId))
    ),
    ...(previewDistributionResult.assignments || [])
  ];

  return {
    ...previewDistributionResult,
    assignments: mergedAssignments
  };
};

const TaskDistribution = () => {
  const notificationAnchorRef = useRef(null);
  const notificationTimeoutRef = useRef(null);
  const notificationTimerStartedAtRef = useRef(0);
  const notificationRemainingMsRef = useRef(NOTIFICATION_DURATION_MS);
  const disabledTeamIdsRef = useRef(new Set());
  const [date, setDate] = useState('');
  const [teams, setTeams] = useState([]);
  const [isAutomateModalOpen, setIsAutomateModalOpen] = useState(false);
  const [teamDisables, setTeamDisables] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [fixedTaskIds, setFixedTaskIds] = useState([]);
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState([]);
  const [distributionResult, setDistributionResult] = useState(null);
  const [confirmedDistributionResult, setConfirmedDistributionResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingTeamDisables, setLoadingTeamDisables] = useState(false);
  const [isDateTransitioning, setIsDateTransitioning] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isRefreshingConfirmedRoutes, setIsRefreshingConfirmedRoutes] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDiscardingPreview, setIsDiscardingPreview] = useState(false);
  const [isRemovingPreassignment, setIsRemovingPreassignment] = useState(false);
  const [pendingUnpinTask, setPendingUnpinTask] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [isNotificationHovered, setIsNotificationHovered] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [visibleRouteTeamIds, setVisibleRouteTeamIds] = useState([]);
  const dateOptions = useMemo(
    () => buildDateOptions(new Date(), MAX_DISTRIBUTION_LOOKAHEAD_DAYS + 1),
    []
  );

  const { taskTypes } = useTaskTypes();

  const clearNotificationDismissTimer = () => {
    if (notificationTimeoutRef.current != null) {
      window.clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
  };

  const scheduleNotificationDismiss = (delayMs = NOTIFICATION_DURATION_MS) => {
    clearNotificationDismissTimer();

    if (!notification.message) {
      notificationRemainingMsRef.current = NOTIFICATION_DURATION_MS;
      return;
    }

    if (delayMs <= 0) {
      notificationRemainingMsRef.current = NOTIFICATION_DURATION_MS;
      setNotification({ type: '', message: '' });
      return;
    }

    notificationTimerStartedAtRef.current = Date.now();
    notificationRemainingMsRef.current = delayMs;
    notificationTimeoutRef.current = window.setTimeout(() => {
      notificationTimeoutRef.current = null;
      notificationRemainingMsRef.current = NOTIFICATION_DURATION_MS;
      setNotification({ type: '', message: '' });
    }, delayMs);
  };

  const clearPreview = () => {
    setDistributionResult(null);
  };

  const clearPreviewForCurrentDate = () => {
    clearPreview();
  };

  const updateConfig = (field, value) => {
    setConfig((current) => buildFixedConfig({
      ...current,
      [field]: value
    }));
    clearPreviewForCurrentDate();
  };

  const applyOptimizationCriterionFromResponse = (nextDistributionResult) => {
    const appliedOptimizationCriterion = getAppliedOptimizationCriterion(nextDistributionResult);
    if (!appliedOptimizationCriterion) {
      return;
    }

    setConfig((current) => buildFixedConfig({
      ...current,
      optimizationCriterion: appliedOptimizationCriterion
    }));
  };

  const applyCachedPreview = (preview, tasks) => {
    const requestedTaskIds = getPreviewTaskIds(preview);
    const requestedTaskIdSet = new Set(requestedTaskIds.map(normalizeId));
    const nextTaskTypeIds = [...new Set(
      tasks
        .filter((task) => requestedTaskIdSet.has(normalizeId(task.id)))
        .map(getTaskTypeIdentifier)
        .filter(Boolean)
    )];
    const enabledTasks = tasks.filter((task) => {
      const taskTeamId = normalizeTaskTeamId(task.teamId ?? task.team?.id);
      return !taskTeamId || !disabledTeamIdsRef.current.has(taskTeamId);
    });
    const nextSelectedTaskIds = enabledTasks
      .map((task) => task.id)
      .filter((taskId) => requestedTaskIdSet.has(normalizeId(taskId)));
    const previewTeamIds = (preview?.assignments || [])
      .map((assignment) => assignment?.teamId)
      .filter(Boolean);
    const previewFixedTaskIds = (preview?.assignments || []).flatMap((assignment) => (
      (assignment?.stops || [])
        .filter((stop) => stop?.type === 'TASK' && stop.taskId && stop.isFixed === true)
        .map((stop) => stop.taskId)
    ));

    setConfig(buildFixedConfig({
      ...(preview?.metadata?.appliedConfiguration || {})
    }));
    setSelectedTeamIds(
      previewTeamIds
        .filter((teamId) => !disabledTeamIdsRef.current.has(normalizeId(teamId)))
    );
    setSelectedTaskTypeIds(nextTaskTypeIds);
    setSelectedTaskIds(nextSelectedTaskIds);
    setFixedTaskIds(
      normalizeFixedTaskIds(
        previewFixedTaskIds,
        tasks,
        nextSelectedTaskIds
      )
    );
    setDistributionResult(preview);
  };

  const refreshPendingTasks = async (scheduledDate, {
    restoreCachedPreview = false,
    restoreConfirmedDistribution = false,
    isActive = () => true
  } = {}) => {
    const response = await taskService.getTasks({
      ...TASKS_QUERY,
      scheduledDate
    });
    const nextTasks = Array.isArray(response.tasks) ? response.tasks : [];

    if (!isActive()) {
      return nextTasks;
    }

    setAvailableTasks(nextTasks);
    let restoredPreview = false;

    let confirmedDistribution = null;
    if (restoreConfirmedDistribution) {
      confirmedDistribution = await taskService.getConfirmedTaskDistribution({ date: scheduledDate });
      if (!isActive()) {
        return nextTasks;
      }

      setConfirmedDistributionResult(confirmedDistribution);
      applyOptimizationCriterionFromResponse(confirmedDistribution);
    } else {
      setConfirmedDistributionResult(null);
    }

    if (restoreCachedPreview) {
      const cachedPreview = await taskService.getCachedTaskDistributionPreview({ date: scheduledDate });
      if (!isActive()) {
        return nextTasks;
      }

      if (
        cachedPreview
        && canRestoreCachedPreview(cachedPreview, nextTasks)
        && shouldRestoreCachedPreview(cachedPreview, confirmedDistribution)
      ) {
        applyCachedPreview(cachedPreview, nextTasks);
        restoredPreview = true;
      }
    }

    if (!restoredPreview) {
      setSelectedTaskIds([]);
      setFixedTaskIds([]);
      setDistributionResult(null);
    }
    return nextTasks;
  };

  useEffect(() => {
    let active = true;

    const fetchTeams = async () => {
      try {
        const teamList = await teamService.getTeams({ hasGeoData: false });
        if (!active) {
          return;
        }

        setTeams(Array.isArray(teamList) ? teamList : []);
      } catch (error) {
        if (active) {
          setNotification({ type: 'error', message: error.message });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchTeams();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!date) {
      disabledTeamIdsRef.current = new Set();
      setTeamDisables([]);
      setLoadingTeamDisables(false);
      return undefined;
    }

    let active = true;

    const fetchTeamDisables = async () => {
      try {
        setLoadingTeamDisables(true);
        const response = await teamDisableService.getDisables({
          date,
          pageCurrent: 0,
          pageSize: 5000
        });
        if (!active) {
          return;
        }

        const nextDisables = Array.isArray(response.disables) ? response.disables : [];
        const nextDisabledTeamIds = new Set(
          nextDisables
            .map((disable) => normalizeId(disable.teamId ?? disable.team?.id))
            .filter(Boolean)
        );
        disabledTeamIdsRef.current = nextDisabledTeamIds;
        setTeamDisables(nextDisables);

        setSelectedTeamIds((current) => (
          current.filter((teamId) => !nextDisabledTeamIds.has(normalizeId(teamId)))
        ));
        setSelectedTaskIds((current) => {
          const taskById = new Map(availableTasks.map((task) => [normalizeId(task.id), task]));
          return current.filter((taskId) => {
            const task = taskById.get(normalizeId(taskId));
            const taskTeamId = normalizeTaskTeamId(task?.teamId ?? task?.team?.id);
            return !taskTeamId || !nextDisabledTeamIds.has(taskTeamId);
          });
        });
      } catch (error) {
        if (!active) {
          return;
        }

        disabledTeamIdsRef.current = new Set();
        setTeamDisables([]);
        setNotification({
          type: 'error',
          message: error.message || 'No se pudo validar la disponibilidad de los equipos.'
        });
      } finally {
        if (active) {
          setLoadingTeamDisables(false);
        }
      }
    };

    fetchTeamDisables();

    return () => {
      active = false;
    };
  }, [date]);

  useEffect(() => {
    if (!teams.length || loadingTeamDisables) {
      return;
    }

    setSelectedTeamIds((current) => {
      const nextValues = current.filter(
        (teamId) => !disabledTeamIdsRef.current.has(normalizeId(teamId))
      );
      return areIdArraysEqual(current, nextValues) ? current : nextValues;
    });
  }, [teams, teamDisables, loadingTeamDisables]);

  useEffect(() => {
    if (!date) {
      setAvailableTasks([]);
      setSelectedTaskIds([]);
      setFixedTaskIds([]);
      setDistributionResult(null);
      setConfirmedDistributionResult(null);
      setLoadingTasks(false);
      setIsDateTransitioning(false);
      return undefined;
    }

    let active = true;

    const fetchTasks = async () => {
      try {
        setLoadingTasks(true);
        await refreshPendingTasks(date, {
          restoreCachedPreview: true,
          restoreConfirmedDistribution: true,
          isActive: () => active
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setAvailableTasks([]);
        setDistributionResult(null);
        setConfirmedDistributionResult(null);
        setNotification({ type: 'error', message: error.message });
      } finally {
        if (active) {
          setLoadingTasks(false);
          setIsDateTransitioning(false);
        }
      }
    };

    fetchTasks();

    return () => {
      active = false;
    };
  }, [date]);

  const disabledTeamIdSet = new Set(
    teamDisables
      .map((disable) => normalizeId(disable.teamId ?? disable.team?.id))
      .filter(Boolean)
  );
  const distributionAvailableTasks = availableTasks.filter((task) => {
    const taskTeamId = normalizeTaskTeamId(task.teamId ?? task.team?.id);
    return !taskTeamId || !disabledTeamIdSet.has(taskTeamId);
  });
  const filteredTasks = distributionAvailableTasks.filter(
    (task) => taskMatchesSelectedTypes(task, selectedTaskTypeIds)
  );

  const selectedTeamIdSet = new Set(selectedTeamIds.map(normalizeId));
  const selectedTeamTasks = distributionAvailableTasks.filter((task) => (
    hasAssignedTeam(task)
    && selectedTeamIdSet.has(normalizeTaskTeamId(task.teamId ?? task.team?.id))
  ));
  const remainingTasks = distributionAvailableTasks.filter((task) => !hasAssignedTeam(task));
  const hasSelectedTeamTasks = selectedTeamTasks.length > 0;
  const eligibleTasks = hasSelectedTeamTasks
    ? [...selectedTeamTasks, ...remainingTasks]
    : distributionAvailableTasks;
  const eligibleTaskIdSet = new Set(eligibleTasks.map((task) => normalizeId(task.id)));
  const filteredEligibleTasks = filteredTasks.filter((task) => eligibleTaskIdSet.has(normalizeId(task.id)));
  const filteredSelectedTeamTasks = filteredEligibleTasks.filter((task) => (
    hasAssignedTeam(task)
    && selectedTeamIdSet.has(normalizeTaskTeamId(task.teamId ?? task.team?.id))
  ));
  const filteredRemainingTasks = filteredEligibleTasks.filter((task) => !hasAssignedTeam(task));

  const effectiveConfig = buildFixedConfig(config);
  const selectedTeams = teams.filter((team) => selectedTeamIds.includes(team.id));
  const selectedTasks = filteredEligibleTasks.filter((task) => selectedTaskIds.includes(task.id));
  const selectedPriorityPreset = FIXED_ROUTING_PRIORITY_PRESET;
  const { activeDistributionResult, mapDistributionResult } = useMemo(() => {
    const activeResult = distributionResult || confirmedDistributionResult;
    const mapResult = buildMapDisplayDistributionResult(confirmedDistributionResult, distributionResult);
    const colorByTeamId = buildRouteColorMap(
      (mapResult?.assignments || []).map((assignment) => assignment.teamId)
    );

    return {
      activeDistributionResult: colorDistributionRoutes(activeResult, colorByTeamId),
      mapDistributionResult: colorDistributionRoutes(mapResult, colorByTeamId)
    };
  }, [distributionResult, confirmedDistributionResult]);
  const isShowingConfirmedDistribution = !distributionResult && Boolean(confirmedDistributionResult);
  const isShowingPreviewWithConfirmedOverlay = Boolean(distributionResult && confirmedDistributionResult);
  const hasOutdatedConfirmedRoutes = Boolean(
    (confirmedDistributionResult?.assignments || []).some((assignment) => assignment?.isLatest === false)
  );
  const taskById = new Map(availableTasks.map((task) => [normalizeId(task.id), task]));
  const mapArtifacts = buildMapArtifacts(mapDistributionResult, taskById);
  const assignmentByTeamId = new Map(
    (activeDistributionResult?.assignments || []).map((assignment) => [String(assignment.teamId), assignment])
  );
  const routeAssignments = (mapDistributionResult?.assignments || []).filter((assignment) => assignment.route?.geometry);
  const routeAssignmentVisibilityKey = buildRouteAssignmentVisibilityKey(routeAssignments);
  const hasConfirmedPlans = Boolean(confirmedDistributionResult?.assignments?.length);
  const resultDistributionResult = isShowingPreviewWithConfirmedOverlay
    ? mapDistributionResult
    : activeDistributionResult;
  const resultMode = isShowingConfirmedDistribution
    ? 'confirmed'
    : (isShowingPreviewWithConfirmedOverlay ? 'overlay' : 'preview');
  const utilizedAssignments = (resultDistributionResult?.assignments || []).filter(
    (assignment) => countTaskStops(assignment) > 0
  );
  const idleAssignments = resultMode === 'preview'
    ? selectedTeams
        .filter((team) => {
          const assignment = assignmentByTeamId.get(String(team.id));
          return !assignment || countTaskStops(assignment) === 0;
        })
        .map((team) => ({
          teamId: team.id,
          teamName: team.name
        }))
    : [];
  const displayedTeamCount = resultMode === 'preview'
    ? selectedTeamIds.length
    : (resultDistributionResult?.assignments || []).length;
  const isDateViewLoading = isDateTransitioning || loadingTasks;

  const teamItems = teams.map((team) => ({
    value: team.id,
    label: team.name,
    searchText: team.description || '',
    team,
    isDisabled: disabledTeamIdSet.has(normalizeId(team.id)),
    disabledReason: teamDisables.find(
      (disable) => normalizeId(disable.teamId ?? disable.team?.id) === normalizeId(team.id)
    )?.reason || '',
    taskCount: availableTasks.filter(
      (task) => normalizeTaskTeamId(task.teamId ?? task.team?.id) === normalizeId(team.id)
    ).length
  }));

  const taskTypeItems = taskTypes.map((taskType) => ({
    value: taskType.name,
    label: taskType.displayName,
    searchText: taskType.categoryName,
    taskType,
    taskCount: eligibleTasks.filter((task) => (
      normalizeId(getTaskTypeIdentifier(task)) === normalizeId(taskType.name)
    )).length
  }));

  const buildTaskItem = (task) => ({
    value: task.id,
    label: task.title,
    searchText: `${task.taskTypeName || ''} ${task.client?.name || ''} ${task.client?.address || ''} ${task.team?.name || ''}`,
    task
  });
  const taskItems = filteredEligibleTasks.map(buildTaskItem);
  const selectedTeamTaskItems = hasSelectedTeamTasks
    ? filteredSelectedTeamTasks.map(buildTaskItem)
    : taskItems;
  const remainingTaskItems = hasSelectedTeamTasks
    ? filteredRemainingTasks.map(buildTaskItem)
    : [];

  useEffect(() => {
    setFixedTaskIds((current) => {
      const nextFixedTaskIds = normalizeFixedTaskIds(current, filteredEligibleTasks, selectedTaskIds);
      return areIdArraysEqual(current, nextFixedTaskIds) ? current : nextFixedTaskIds;
    });
  }, [filteredEligibleTasks, selectedTaskIds]);

  const handleToggleFixedTask = (taskId) => {
    const task = filteredEligibleTasks.find((currentTask) => currentTask.id === taskId);
    if (task?.fixedTeam === true) {
      return;
    }

    setFixedTaskIds((current) => (
      current.includes(taskId)
        ? current.filter((currentTaskId) => currentTaskId !== taskId)
        : [...current, taskId]
    ));
    clearPreviewForCurrentDate();
  };

  const handleTeamsChange = (values) => {
    const enabledValues = values.filter(
      (teamId) => !disabledTeamIdSet.has(normalizeId(teamId))
    );
    setSelectedTeamIds(enabledValues);
    setSelectedTaskIds([]);
    setFixedTaskIds([]);
    clearPreviewForCurrentDate();
  };

  const handleTaskTypesChange = (values) => {
    setSelectedTaskTypeIds(values);
    const nextFilteredTasks = distributionAvailableTasks.filter(
      (task) => taskMatchesSelectedTypes(task, values)
    );
    const selectedTeamIdSetForFilter = new Set(selectedTeamIds.map(normalizeId));
    const selectedTeamHasTasks = distributionAvailableTasks.some((task) => (
      hasAssignedTeam(task)
      && selectedTeamIdSetForFilter.has(normalizeTaskTeamId(task.teamId ?? task.team?.id))
    ));
    const nextEligibleTaskIdSet = new Set(
      nextFilteredTasks
        .filter((task) => (
          !selectedTeamHasTasks
          || !hasAssignedTeam(task)
          || selectedTeamIdSetForFilter.has(normalizeTaskTeamId(task.teamId ?? task.team?.id))
        ))
        .map((task) => normalizeId(task.id))
    );
    setSelectedTaskIds((current) => (
      current.filter((taskId) => nextEligibleTaskIdSet.has(normalizeId(taskId)))
    ));
    clearPreviewForCurrentDate();
  };

  const handleTasksChange = (values) => {
    const addedTaskIdSet = new Set(
      values
        .filter((taskId) => !selectedTaskIds.includes(taskId))
        .map(normalizeId)
    );
    const requiredTeamIdSet = new Set(
      filteredEligibleTasks
        .filter((task) => addedTaskIdSet.has(normalizeId(task.id)) && hasAssignedTeam(task))
        .map((task) => normalizeTaskTeamId(task.teamId ?? task.team?.id))
        .filter(Boolean)
    );

    if (requiredTeamIdSet.size) {
      setSelectedTeamIds((current) => {
        const currentTeamIdSet = new Set(current.map(normalizeId));
        const nextValues = [...current];
        teams.forEach((team) => {
          const normalizedTeamId = normalizeId(team.id);
          if (requiredTeamIdSet.has(normalizedTeamId) && !currentTeamIdSet.has(normalizedTeamId)) {
            nextValues.push(team.id);
          }
        });
        return nextValues;
      });
    }

    setSelectedTaskIds(values);
    clearPreviewForCurrentDate();
  };

  useEffect(() => {
    const nextVisibleRouteTeamIds = routeAssignments.map((assignment) => String(assignment.teamId));
    setVisibleRouteTeamIds((current) => (
      areIdArraysEqual(current, nextVisibleRouteTeamIds) ? current : nextVisibleRouteTeamIds
    ));
  }, [routeAssignmentVisibilityKey]);

  useEffect(() => {
    if (!date || !hasConfirmedPlans || loadingTasks || isDateTransitioning
      || isCalculating || isConfirming || isRefreshingConfirmedRoutes
      || isDiscardingPreview || isRemovingPreassignment) {
      return undefined;
    }

    let active = true;
    let inFlight = false;
    const refreshConfirmedRoutes = async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;

      try {
        const nextDistribution = await taskService.getConfirmedTaskDistribution({ date });
        if (active) {
          setConfirmedDistributionResult((current) => (
            JSON.stringify(current) === JSON.stringify(nextDistribution) ? current : nextDistribution
          ));
        }
      } catch (error) {
        console.warn('No se pudieron actualizar los recorridos confirmados:', error);
      } finally {
        inFlight = false;
      }
    };

    const interval = window.setInterval(refreshConfirmedRoutes, CONFIRMED_ROUTES_REFRESH_MS);
    window.addEventListener('focus', refreshConfirmedRoutes);
    document.addEventListener('visibilitychange', refreshConfirmedRoutes);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshConfirmedRoutes);
      document.removeEventListener('visibilitychange', refreshConfirmedRoutes);
    };
  }, [date, hasConfirmedPlans, loadingTasks, isDateTransitioning, isCalculating,
    isConfirming, isRefreshingConfirmedRoutes, isDiscardingPreview, isRemovingPreassignment]);

  useEffect(() => {
    if (notification.type !== 'error' || !notification.message) {
      return;
    }

    notificationAnchorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [notification.type, notification.message]);

  useEffect(() => {
    clearNotificationDismissTimer();

    if (!notification.message) {
      notificationRemainingMsRef.current = NOTIFICATION_DURATION_MS;
      setIsNotificationHovered(false);
      return undefined;
    }

    notificationRemainingMsRef.current = NOTIFICATION_DURATION_MS;
    setIsNotificationHovered(false);
    scheduleNotificationDismiss(NOTIFICATION_DURATION_MS);

    return () => {
      clearNotificationDismissTimer();
    };
  }, [notification.type, notification.message]);

  useEffect(() => {
    return () => {
      clearNotificationDismissTimer();
    };
  }, []);

  const handleNotificationMouseEnter = () => {
    if (!notification.message) {
      return;
    }

    setIsNotificationHovered(true);

    if (notificationTimeoutRef.current == null) {
      return;
    }

    const elapsedMs = Date.now() - notificationTimerStartedAtRef.current;
    notificationRemainingMsRef.current = Math.max(0, notificationRemainingMsRef.current - elapsedMs);
    clearNotificationDismissTimer();
  };

  const handleNotificationMouseLeave = () => {
    if (!notification.message) {
      return;
    }

    setIsNotificationHovered(false);
    scheduleNotificationDismiss(notificationRemainingMsRef.current || NOTIFICATION_DURATION_MS);
  };

  const handleDateChange = (nextDate) => {
    if (nextDate === date) {
      return;
    }

    setIsDateTransitioning(true);
    setDate(nextDate);
    setDistributionResult(null);
    setConfirmedDistributionResult(null);
  };

  const handleCalculate = async () => {
    if (!selectedTeamIds.length) {
      setNotification({ type: 'error', message: 'Debe seleccionar al menos un equipo.' });
      return;
    }

    if (!selectedTaskIds.length) {
      setNotification({ type: 'error', message: 'Debe seleccionar al menos una tarea.' });
      return;
    }

    try {
      setIsCalculating(true);
      setNotification({ type: '', message: '' });

      const payload = await taskService.getTaskDistributionPreview({
        date,
        teamIds: selectedTeamIds,
        taskIds: selectedTaskIds,
        fixedTaskIds,
        taskTypeIds: selectedTaskTypeIds,
        redistributionTeamIds: selectedTeamIds,
        configuration: effectiveConfig
      });

      setDistributionResult(payload);
      applyOptimizationCriterionFromResponse(payload);
      const redisWarningMessage = getRedisPersistenceWarningMessage(payload.warnings);

      if (redisWarningMessage) {
        setNotification({
          type: 'success',
          message: redisWarningMessage
        });
      }
    } catch (error) {
      setDistributionResult(null);
      setNotification({ type: 'error', message: error.message });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleRefreshConfirmedRoutes = async () => {
    try {
      setIsRefreshingConfirmedRoutes(true);
      setNotification({ type: '', message: '' });

      const confirmedDistribution = await taskService.getConfirmedTaskDistribution({
        date,
        refreshOutdated: true
      });

      const warningSuffix = Array.isArray(confirmedDistribution?.warnings) && confirmedDistribution.warnings.length
        ? ` ${confirmedDistribution.warnings.join(' ')}`
        : '';

      setConfirmedDistributionResult(confirmedDistribution);
      applyOptimizationCriterionFromResponse(confirmedDistribution);
      setNotification({
        type: 'success',
        message: `Recorridos confirmados actualizados.${warningSuffix}`
      });
    } catch (error) {
      setNotification({ type: 'error', message: error.message });
    } finally {
      setIsRefreshingConfirmedRoutes(false);
    }
  };

  const handleConfirm = async () => {
    if (!distributionResult) {
      return;
    }

    try {
      setIsConfirming(true);

      const payload = await taskService.confirmTaskDistribution(
        buildConfirmationPayload({
          date,
          distributionResult,
          configuration: effectiveConfig,
          selectedTeamIds,
          selectedTaskIds,
          fixedTaskIds,
          selectedTaskTypeIds
        })
      );

      applyOptimizationCriterionFromResponse(payload);

      const warningSuffix = Array.isArray(payload.warnings) && payload.warnings.length
        ? ` ${payload.warnings.join(' ')}`
        : '';

      setNotification({
        type: 'success',
        message: `${payload.message || 'Distribución confirmada correctamente.'}${warningSuffix}`
      });
      setDistributionResult(null);
      await refreshPendingTasks(date, {
        restoreConfirmedDistribution: true
      });
    } catch (error) {
      setNotification({ type: 'error', message: error.message });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDiscardPreview = async () => {
    try {
      setIsDiscardingPreview(true);
      setNotification({ type: '', message: '' });

      const payload = await taskService.discardCachedTaskDistributionPreview({ date });
      setDistributionResult(null);

      const warningSuffix = Array.isArray(payload?.warnings) && payload.warnings.length
        ? ` ${payload.warnings.join(' ')}`
        : '';

      setNotification({
        type: payload?.deleted ? 'success' : 'error',
        message: payload?.deleted
          ? `Previsualizacion descartada correctamente.${warningSuffix}`
          : `La previsualizacion se oculto en esta sesion, pero no se pudo borrar la cache persistida.${warningSuffix}`
      });
    } catch (error) {
      setNotification({ type: 'error', message: error.message });
    } finally {
      setIsDiscardingPreview(false);
    }
  };

  const handleRemovePreassignment = async () => {
    if (!pendingUnpinTask) {
      return;
    }

    try {
      setIsRemovingPreassignment(true);
      await taskService.reassignTaskTeam(pendingUnpinTask.id, null);
      await refreshPendingTasks(date);
      clearPreviewForCurrentDate();
      setNotification({ type: 'success', message: 'Pre-asignacion removida correctamente.' });
      setPendingUnpinTask(null);
    } catch (error) {
      setNotification({ type: 'error', message: error.message });
    } finally {
      setIsRemovingPreassignment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-amber" />
      </div>
    );
  }

  return (
    <div ref={notificationAnchorRef} className="min-w-0 space-y-6">
      <PageHeader
        title="Distribución de Tareas"
        icon={<ClipboardText size={24} weight="duotone" />}
        subtitle="Calcula la asignación automática por equipo, previsualiza rutas y confirma la distribución final."
        actionButton={{
          label: 'Automatizar',
          icon: <Plus size={18} />,
          onClick: () => setIsAutomateModalOpen(true)
        }}
      />

      {notification.message && (
        <div
          onMouseEnter={handleNotificationMouseEnter}
          onMouseLeave={handleNotificationMouseLeave}
          className={`rounded-2xl border px-4 py-3 text-sm transition-opacity ${
            notification.type === 'error'
              ? 'border-rose-500/50 bg-rose-500/10 text-rose-100'
              : 'border-emerald-500/50 bg-emerald-500/10 text-emerald-100'
          } ${isNotificationHovered ? 'opacity-100' : 'opacity-95'}`}
        >
          {notification.message}
        </div>
      )}

      <div className="min-w-0 space-y-6">
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-4">
            <TaskDistributionStepper
              date={date}
              dateOptions={dateOptions}
              config={effectiveConfig}
              selectedPriorityPreset={selectedPriorityPreset}
              teamItems={teamItems}
              selectedTeamIds={selectedTeamIds}
              taskTypeItems={taskTypeItems}
              selectedTaskTypeIds={selectedTaskTypeIds}
              taskItems={taskItems}
              selectedTeamTaskItems={selectedTeamTaskItems}
              remainingTaskItems={remainingTaskItems}
              hasSelectedTeamTasks={hasSelectedTeamTasks}
              isLoadingTeamAvailability={loadingTeamDisables}
              selectedTaskIds={selectedTaskIds}
              fixedTaskIds={fixedTaskIds}
              isDateViewLoading={isDateViewLoading}
              isCalculating={isCalculating}
              onDateChange={handleDateChange}
              onConfigChange={updateConfig}
              onTeamsChange={handleTeamsChange}
              onTaskTypesChange={handleTaskTypesChange}
              onTasksChange={handleTasksChange}
              onToggleFixedTask={handleToggleFixedTask}
              onRequestRemovePreassignment={setPendingUnpinTask}
              onCalculate={handleCalculate}
            />
          </div>

          <div className="min-w-0 space-y-6">
            {isDateViewLoading ? (
              <div className="flex min-h-[720px] items-center justify-center rounded-2xl border border-line bg-surface/85 px-6 py-10 shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
                <div className="flex max-w-sm flex-col items-center gap-4 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-amber/30 bg-[--app-sidebar-active]">
                    <ArrowsClockwise size={24} className="animate-spin text-accent-text" />
                  </div>
                  <div className="text-base font-semibold text-content">Cargando recorridos del dia</div>
                  <div className="text-sm text-content-muted">
                    Actualizando mapa, resultados y tareas para {date}.
                  </div>
                </div>
              </div>
            ) : (
            <>
              <TaskDistributionMapSection
                distributionResult={mapDistributionResult}
                simulationDistributionResult={activeDistributionResult}
                resultMode={isShowingConfirmedDistribution ? 'confirmed' : 'preview'}
                mapArtifacts={mapArtifacts}
                isCalculating={isCalculating || isRefreshingConfirmedRoutes}
                routeAssignments={routeAssignments}
                selectedTasks={selectedTasks}
                showConfirmedOverlayNotice={isShowingPreviewWithConfirmedOverlay}
                showRefreshConfirmedRoutes={Boolean(confirmedDistributionResult && hasOutdatedConfirmedRoutes)}
                visibleRouteTeamIds={visibleRouteTeamIds}
                onRefreshConfirmedRoutes={handleRefreshConfirmedRoutes}
                onToggleRouteVisibility={(teamId) => {
                  const normalizedTeamId = String(teamId);
                  setVisibleRouteTeamIds((current) => (
                    current.includes(normalizedTeamId)
                      ? current.filter((visibleTeamId) => visibleTeamId !== normalizedTeamId)
                      : [...current, normalizedTeamId]
                  ));
                }}
                onShowAllRoutes={() => {
                  setVisibleRouteTeamIds(routeAssignments.map((assignment) => String(assignment.teamId)));
                }}
                onHideAllRoutes={() => {
                  setVisibleRouteTeamIds([]);
                }}
              />

              {distributionResult && (
                <div className="flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    disabled={isDiscardingPreview}
                    onClick={handleDiscardPreview}
                    className="inline-flex items-center gap-2 rounded-2xl border border-line bg-surface px-5 py-3 text-sm font-semibold text-content transition hover:border-line hover:text-content"
                  >
                    {isDiscardingPreview ? <ArrowsClockwise size={18} className="animate-spin" /> : <X size={18} />}
                    {isDiscardingPreview ? 'Descartando...' : 'Descartar'}
                  </button>
                  <button
                    type="button"
                    disabled={isConfirming || isDiscardingPreview}
                    onClick={handleConfirm}
                    className="inline-flex items-center gap-2 rounded-2xl border border-emerald-500/40 bg-emerald-500/15 px-5 py-3 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isConfirming ? <ArrowsClockwise size={18} className="animate-spin" /> : <Check size={18} />}
                    {isConfirming ? 'Confirmando...' : 'Confirmar'}
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {!isDateViewLoading && (
          <TaskDistributionResultsSection
            distributionResult={resultDistributionResult}
            resultMode={resultMode}
            utilizedAssignments={utilizedAssignments}
            selectedTeamCount={displayedTeamCount}
            idleAssignments={idleAssignments}
            taskById={taskById}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(pendingUnpinTask)}
        title="Quitar pre-asignación"
        message={pendingUnpinTask ? `La tarea "${pendingUnpinTask.title}" dejará de estar preasignada a su equipo actual y volverá a quedar libre para la optimización. ¿Quieres continuar?` : ''}
        confirmText={isRemovingPreassignment ? 'Quitando...' : 'Quitar'}
        cancelText="Cancelar"
        onCancel={() => {
          if (!isRemovingPreassignment) {
            setPendingUnpinTask(null);
          }
        }}
        onConfirm={() => {
          if (!isRemovingPreassignment) {
            handleRemovePreassignment();
          }
        }}
      />

      <TaskDistributionAutomateModal
        isOpen={isAutomateModalOpen}
        onClose={() => setIsAutomateModalOpen(false)}
        teams={teams}
      />
    </div>
  );
};

export default TaskDistribution;
